/**
 * Extracts and returns the text content from a given DOM node.
 * 
 * This function handles different types of nodes and HTML elements,
 * converting them into a plain text representation. It processes
 * text nodes directly and handles various HTML tags such as `<br>`,
 * `<p>`, `<ul>`, `<ol>`, `<li>`, `<b>`, `<strong>`, `<i>`, `<em>`,
 * `<u>`, `<span>`, and `<img>` to generate a meaningful text output.
 * 
 * @param node - The DOM node from which to extract text content.
 * @returns The plain text representation of the node's content.
 */
function getTextFromNode(node: ChildNode): string {
  let result = "";

  // Se for apenas texto, retornamos direto
  if (node.nodeType === Node.TEXT_NODE) {
    return node.nodeValue || "";
  }

  // Se for um elemento, tratamos de acordo com a tag
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    switch (tagName) {
      case "br":
        result += "\n";
        break;

      case "p":
        result += getTextContent(element) + "\n";
        break;

      case "ul":
      case "ol":
        result += getTextContent(element) + "\n";
        break;

      case "li":
        result += "- " + getTextContent(element) + "\n";
        break;

      case "b":
      case "strong":
        result += getTextContent(element).toUpperCase();
        break;

      case "i":
      case "em":
        result += "_" + getTextContent(element) + "_";
        break;

      case "u":
        result += "_" + getTextContent(element) + "_";
        break;

      case "span":
        result += getTextContent(element);
        break;

      case "img":
        // Para a conversão em texto, podemos manter a indicação de imagem
        result += "[imagem]";
        break;

      default:
        // Se for uma tag que não tratamos, apenas pegamos o texto interno
        result += getTextContent(element);
        break;
    }
  }

  return result;
}

/**
 * Extracts and concatenates the text content from all child nodes of a given HTML element.
 *
 * @param {HTMLElement} element - The HTML element from which to extract text content.
 * @returns {string} The concatenated text content of all child nodes.
 */
function getTextContent(element: HTMLElement): string {
  let text = "";
  element.childNodes.forEach((child) => {
    text += getTextFromNode(child);
  });
  return text;
}

/**
 * parseHtmlToText
 * @param htmlString - String HTML de entrada
 * @param sanitizeInput - Função que sanitiza o conteúdo (DOMPurify, por ex.)
 * @returns Texto convertido
 */
export function parseHtmlToText(
  htmlString: string,
  sanitizeInput: (input: string) => string
): string {
  if (!htmlString) return "";

  // 1) Sanitiza
  const sanitizedHtml = sanitizeInput(htmlString);

  // 2) Converte em DOM
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitizedHtml, "text/html");

  // 3) Extrai texto
  const text = getTextContent(doc.body);

  // 4) Retorna texto formatado
  return text.trim();
}

/**
 * renderHtmlContent
 * @param htmlString - String HTML de entrada
 * @param sanitizeInput - Função que sanitiza o conteúdo (por exemplo, usando DOMPurify)
 * @returns O HTML sanitizado, pronto para ser renderizado com dangerouslySetInnerHTML
 *
 * Nesta função ajustamos o tratamento da tag <img> para exibir uma preview.
 * Após o parse, percorremos todas as imagens e adicionamos uma classe e estilos inline
 * para controlar seu tamanho, garantindo que a imagem seja exibida como preview.
 */
export function renderHtmlContent(
  htmlString: string,
  sanitizeInput: (input: string) => string
): string {
  if (!htmlString) return "";

  // 1) Sanitiza o HTML, mantendo as tags permitidas
  const sanitizedHtml = sanitizeInput(htmlString);

  // 2) Utilizamos o DOMParser para que o navegador normalize a estrutura,
  //    caso haja algum ajuste necessário (opcional, mas recomendado)
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitizedHtml, "text/html");

  // 3) Ajusta as imagens para exibição em preview
  doc.querySelectorAll("img").forEach((img) => {
    // Adiciona uma classe para aplicar estilos via CSS, se necessário
    img.classList.add("imgPreview");

    // Define estilos inline para limitar o tamanho da imagem (ajuste conforme sua necessidade)
    img.style.maxWidth = "200px";
    img.style.maxHeight = "200px";
    img.style.objectFit = "contain";
  });

  // 4) Retorna o innerHTML do body, que contém o HTML formatado conforme renderizado pelo navegador
  return doc.body.innerHTML;
}
